import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Form, Icon, Modal } from "semantic-ui-react";

import { ReactTable } from "components";
import { useReplaceTyreMutation } from "modules/IncompleteTyresDashboard/components/EditTyreModal/components/ReplaceTyreConfirm/hooks";
import "modules/IncompleteTyresDashboard/components/EditTyreModal/components/ReplaceTyreConfirm/ReplaceTyreConfirm.scss";
import { getTyreSeasonName } from "modules/IncompleteTyresDashboard/util";

import type { AccessorColumnDef } from "@tanstack/react-table";
import type { Tyre } from "models";

type ReplaceTyreConfirmProps = {
  tyre: Tyre | null;
  replacementTyre: Tyre | null;
  onClose: () => void;
  onReplaced: () => void;
};

export const ReplaceTyreConfirm = ({ tyre, replacementTyre, onClose, onReplaced }: ReplaceTyreConfirmProps) => {
  const { t } = useTranslation();

  const columns = useMemo<AccessorColumnDef<Tyre>[]>(
    () => [
      {
        id: "manufacturer",
        size: 120,
        accessorKey: "manufacturer",
        header: () => <span className="no-wrap-text">{t("v8_manufacturer", "Manufacturer")}</span>
      },
      {
        id: "width",
        size: 50,
        accessorKey: "width",
        header: () => <span className="no-wrap-text">{t("v8_width", "Width")}</span>
      },
      {
        id: "height",
        size: 50,
        accessorKey: "height",
        header: () => <span className="no-wrap-text">{t("v8_height", "Height")}</span>
      },
      {
        id: "size",
        size: 50,
        accessorKey: "size",
        header: () => <span className="no-wrap-text">{t("v8_size", "Size")}</span>
      },
      {
        id: "speed",
        size: 50,
        accessorKey: "speed",
        header: () => <span className="no-wrap-text">{t("v8_speed", "Speed")}</span>
      },
      {
        id: "season",
        size: 100,
        accessorKey: "season",
        header: () => <span className="no-wrap-text">{t("v8_season", "Season")}</span>,
        cell: ({ row: { original } }) => getTyreSeasonName(original.season, t)
      },
      {
        id: "ean",
        size: 120,
        accessorKey: "ean",
        header: () => <span className="no-wrap-text">EAN</span>
      },
      {
        id: "description",
        size: 150,
        accessorKey: "description",
        header: () => <span className="no-wrap-text">{t("v8_description", "Description")}</span>
      }
    ],
    [t]
  );

  const { mutateAsync: replaceTyre, isPending } = useReplaceTyreMutation();

  const handleConfirm = async () => {
    if (!tyre || !replacementTyre) return;

    await replaceTyre({ complete_tyre_id: replacementTyre.id, incomplete_tyre_id: tyre.id });
    onReplaced();
  };

  return (
    <Modal open={Boolean(tyre && replacementTyre)} onClose={onClose} size="small" className="ReplaceTyreConfirm">
      <Modal.Header className="padding-container warning">
        <div className="header-title">{t("v8_are_you_sure", "Are you sure?")}</div>
      </Modal.Header>

      <Modal.Content className="padding-container">
        {tyre && (
          <div className="tyre-section">
            <div className="tyre-section-label">{t("v8_incomplete_tyre", "Incomplete tyre")}</div>
            <ReactTable columns={columns} data={[tyre]} />
          </div>
        )}

        {replacementTyre && (
          <div className="tyre-section">
            <div className="tyre-section-label">{t("v8_replace_with", "Replace with")}</div>
            <ReactTable columns={columns} data={[replacementTyre]} />
          </div>
        )}
      </Modal.Content>

      <Modal.Actions className="padding-container">
        <div className="actions-container">
          <div className="save-cancel-buttons-container">
            <Form.Button type="button" className="light" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Form.Button>
            <Form.Button type="button" color="orange" disabled={isPending} loading={isPending} onClick={handleConfirm}>
              {t("v8_confirm", "Confirm")}
              <Icon className="check" />
            </Form.Button>
          </div>
        </div>
      </Modal.Actions>
    </Modal>
  );
};
